import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiCheck } from 'react-icons/fi';

// Share Registry › All holders filter chip with its pop-up (Figma 532:3287, note 537:2177): pick an option with its
// live holder count, then Apply. Counts are worked out by the caller from the listing already on the page.

export interface FilterOption {
    value: string;
    label: string;
    chip?: string; // shorter text for the chip, e.g. "1–1,000"
    count: number | null; // null shows "—" (no source for this option yet)
    disabled?: boolean;
    hint?: string; // replaces the count, e.g. "Not connected yet"
}

interface Props {
    label: string; // chip prefix, e.g. "Holding Size"
    heading: string; // pop-up caption
    value: string;
    options: FilterOption[];
    onApply: (value: string) => void;
}

const FilterPopover: React.FC<Props> = ({ label, heading, value, options, onApply }) => {
    const [open, setOpen] = useState(false);
    const [pending, setPending] = useState(value);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown);
        window.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); window.removeEventListener('keydown', onKey); };
    }, [open]);

    const toggle = () => { setPending(value); setOpen(!open); };
    const apply = (v: string) => { onApply(v); setOpen(false); };
    const current = options.find(o => o.value === value);

    return <div className="holder-filter" ref={ref}>
        <button type="button" className={`holder-filter-chip${open ? ' open' : ''}${value !== options[0]?.value ? ' set' : ''}`} aria-expanded={open} onClick={toggle}>
            {label}: {current?.chip ?? current?.label ?? value} <FiChevronDown />
        </button>
        {open && <div className="holder-filter-pop" role="dialog" aria-label={heading}>
            <span className="caption">{heading}</span>
            <div className="options" role="radiogroup">
                {options.map(o => <button type="button" key={o.value} role="radio" aria-checked={pending === o.value} disabled={o.disabled}
                    className={`holder-filter-option${pending === o.value ? ' on' : ''}`} onClick={() => setPending(o.value)}>
                    <span className="radio" />
                    <span className="t">{o.label}</span>
                    <span className="n">{o.hint ?? (o.count === null ? '—' : `${o.count.toLocaleString()} holder${o.count === 1 ? '' : 's'}`)}</span>
                </button>)}
            </div>
            <div className="f">
                <button type="button" className="clear" onClick={() => apply(options[0].value)}>Clear</button>
                <button type="button" className="btn-primary" onClick={() => apply(pending)}><FiCheck /> Apply</button>
            </div>
        </div>}
    </div>;
};

export default FilterPopover;
